import { ErrorHandler, inject, Injectable } from '@angular/core';
import { AnalyticsUtils } from '@analytics/utils';
import type {
  GetProductsRequestResponse,
  ProductData,
  StoreFunnelSettingsInterface,
  StoreFunnelStep,
} from '@innogy/feh/models';
import type { StoreProductData } from '@legacy/common-ui-models';
import { getIsExperienceEditorActive } from '@core/jss-routing';
import {
  registerSCProgressiveFormAction,
  resetGenericProgressiveFormAction,
} from '@innogy/shared/progressive-form/sitecore';
import { resetProgressiveForm } from '@innogy/shared/progressive-form';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, TimeoutError } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  timeout,
} from 'rxjs/operators';
import type { BaseActionType } from '@essent/common';
import { createFunnelEffectCreators } from '@innogy/feh/shared';
import { FehApiActions } from '@feh/shared-api';

import {
  handleStoreFunnelGenericErrorAction,
  handleStoreFunnelPrivateErrorAction,
  initializeStoreFunnelStepAction,
  mintStoreTrackingIdAction,
  onStoreFunnelNavigationAction,
  resetStoreProductSelectionAction,
  setStoreFunnelInitializedAction,
  storeAssociatedGenericFormIdAction,
} from './store-funnel.actions';
import { selectStoreFunnelSettings } from './store-funnel.selectors';
import { transformToGetProductsRequestResponse } from './store-products-mapper';

@Injectable()
export class StoreFunnelEffects {
  private readonly actions$ = inject(Actions);
  private readonly store$ = inject(Store);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly analyticsUtils = inject(AnalyticsUtils);

  funnelSettings$ = this.store$.select(selectStoreFunnelSettings);
  isXpEditorActive$ = this.store$.select(getIsExperienceEditorActive);

  private readonly effects = createFunnelEffectCreators<
    StoreFunnelStep,
    StoreFunnelSettingsInterface,
    StoreProductData
  >(
    this.actions$,
    this.funnelSettings$,
    this.isXpEditorActive$,
    'productOverviewPage',
    'productOverview',
    this.analyticsUtils,
    {
      initializeFunnelStepAction: initializeStoreFunnelStepAction,
      handleFunnelPrivateErrorAction: handleStoreFunnelPrivateErrorAction,
      onFunnelNavigationAction: onStoreFunnelNavigationAction,
      setFunnelInitializedAction: setStoreFunnelInitializedAction,
      handleFunnelGenericErrorAction: handleStoreFunnelGenericErrorAction,
      mintTrackingIdAction: mintStoreTrackingIdAction,
    }
  );

  public readonly getProductsOnStoreFunnelInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setStoreFunnelInitializedAction),
      map(({ funnelSettings }) => funnelSettings),
      filter((data) => data.id != null),
      mergeMap((settings) => [
        resetStoreProductSelectionAction(),
        settings.products
          ? this.getLocalProducts(settings.products)
          : this.getRemoteProducts(settings.id as string),
      ])
    )
  );

  private getLocalProducts(products: ProductData[]) {
    const transformedProductsPayload =
      this.getTransformedProductsPayload(products);
    return FehApiActions.getBoilerProducts.successAction(
      transformedProductsPayload as any
    );
  }

  private getRemoteProducts(funnelSettingsId: string) {
    return FehApiActions.getBoilerProducts.requestAction({
      payload: { funnelSettingsId },
    });
  }

  private getTransformedProductsPayload(
    products: ProductData[]
  ): BaseActionType<GetProductsRequestResponse> {
    const parsedProducts = transformToGetProductsRequestResponse(products);
    return { payload: parsedProducts };
  }

  /**
   * This effect listens to a navigation from product -> order page as this is the
   * signal to start listening for the registration of a form associated with this funnel.
   * If a form is loaded within 10 seconds, it will register in the funnel settings reducer, so that we can
   * hook into the form submission and post all data to the correct endpoint.
   *
   * If the 10 second timeout is hit, a generic error will be raised to the user and an error will be logged for alerting purposes.
   */
  public readonly hookAssociatedGenericForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onStoreFunnelNavigationAction),
      concatLatestFrom(() => [this.funnelSettings$]),
      filter(
        ([action, { orderPage }]) => action.page?.href === orderPage?.href
      ),
      switchMap(([, { orderPage, id }]) =>
        this.actions$.pipe(
          ofType(registerSCProgressiveFormAction),
          timeout(10000),
          take(1),
          mergeMap(({ formId }) => [
            resetGenericProgressiveFormAction({
              payload: formId,
            }),
            resetProgressiveForm({ formId }),
            storeAssociatedGenericFormIdAction({
              formId,
              instanceId: id as string,
            }),
          ]),
          catchError((err: unknown) => {
            if (err instanceof TimeoutError) {
              this.errorHandler.handleError(
                new Error(
                  `Associating a generic form to the store funnel at "${orderPage?.href}" has timed out`
                )
              );
              return EMPTY;
            } else {
              throw err;
            }
          })
        )
      )
    )
  );

  public readonly initializeStoreFunnelStep$ =
    this.effects.initializeFunnelStepEffectCreator();

  public readonly mintStoreFunnelTrackingId$ =
    this.effects.mintTrackingIdEffectCreator();

  public readonly onStoreFunnelNavigation$ =
    this.effects.onFunnelNavigationEffectCreator();

  public readonly handleStoreFunnelGenericError$ =
    this.effects.handleFunnelGenericErrorEffectCreator();

  public readonly handleStoreFunnelPrivateError$ =
    this.effects.handleFunnelPrivateErrorEffectCreator();
}
